import type * as React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../Dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  confirmText: string;
  cancelText: string;
  closeLabel: string;
  variant?: "destructive" | "neutral";
  loading?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose?: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmText,
  cancelText,
  closeLabel,
  variant = "neutral",
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const isDestructive = variant === "destructive";

  const handleOpenChange = (nextOpen: boolean) => {
    if (loading) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      onClose?.();
    }
  };

  const handleCancel = () => {
    if (loading) return;
    onOpenChange?.(false);
    onClose?.();
  };

  const handleConfirm = () => {
    if (loading || confirmDisabled) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        closeLabel={closeLabel}
        showCloseButton={!loading}
        onEscapeKeyDown={(e) => {
          if (loading) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (loading) e.preventDefault();
        }}
        className={
          isDestructive
            ? "max-h-[85dvh] overflow-y-auto [--focus-ring:var(--color-fail-text)]"
            : "max-h-[85dvh] overflow-y-auto"
        }
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={handleCancel}
          >
            {cancelText}
          </Button>

          <Button
            type="button"
            variant={isDestructive ? "secondary" : "primary"}
            className={
              isDestructive
                ? "border-fail-text text-fail-text hover:bg-container-hover"
                : undefined
            }
            disabled={loading || confirmDisabled}
            onClick={handleConfirm}
          >
            {loading && <Loader2 className="size-4 animate-spin" />}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
